import React, { useEffect, useMemo } from "react";
import {
  ExpectancyEntry,
  ClientAccessScope,
  PortalPatient,
  PortalTherapist,
} from "../api/contracts";
import { useClientExpectancies } from "../hooks/useClientExpectancies";
import { formatPortalDateTime } from "../utils/dateTime";
import {
  ContentSection,
  ExpectancyResultPill,
  PortalDataTable,
  PortalTableColumn,
} from "./DataDisplay";
import { EmptyState } from "./FeedbackStates";

function EmptyCell() {
  return <span className="cp-empty-value">—</span>;
}

function textOrDash(value: string) {
  return value ? value : <EmptyCell />;
}

const predictionResultRows = [
  { result: "Proved false", tone: "info" },
  { result: "Partly true", tone: "success" },
  { result: "Came true", tone: "teal" },
  { result: "Pending", tone: "warning" },
  { result: "Not needed", tone: "neutral" },
] as const;

function formatEntryCount(count: number): string {
  return `${count} ${count === 1 ? "entry" : "entries"}`;
}

function formatResolvedCount(count: number): string {
  return `${count} resolved ${count === 1 ? "prediction" : "predictions"}`;
}

function ExpectancyResultsSummary({
  entries,
}: {
  entries: readonly ExpectancyEntry[];
}) {
  const counts = useMemo(() => {
    const resultCounts = new Map<string, number>();
    entries.forEach((entry) => {
      if (entry.predictionResult) {
        resultCounts.set(
          entry.predictionResult,
          (resultCounts.get(entry.predictionResult) ?? 0) + 1,
        );
      }
    });
    return resultCounts;
  }, [entries]);
  const categorizedCount = predictionResultRows.reduce(
    (total, row) => total + (counts.get(row.result) ?? 0),
    0,
  );
  const resolvedCount =
    (counts.get("Proved false") ?? 0) +
    (counts.get("Partly true") ?? 0) +
    (counts.get("Came true") ?? 0);
  const disprovenCount = counts.get("Proved false") ?? 0;
  const disprovenRate = resolvedCount
    ? Math.round((disprovenCount / resolvedCount) * 100)
    : null;

  return (
    <section
      className="cp-expectancy-results"
      aria-labelledby="cp-expectancy-results-title"
    >
      <header className="cp-expectancy-results__header">
        <h3 id="cp-expectancy-results-title">Prediction Results</h3>
        <p>
          Based on {formatEntryCount(categorizedCount)} with a recorded result.
        </p>
      </header>
      <div className="cp-expectancy-results__content">
        <div className="cp-expectancy-results__metric">
          <strong>{disprovenRate === null ? "—" : `${disprovenRate}%`}</strong>
          <span>Feared prediction disproven</span>
          <small>
            {disprovenCount} of {formatResolvedCount(resolvedCount)}
          </small>
        </div>
        <div
          className="cp-expectancy-results__breakdown"
          role="list"
          aria-label="Prediction result breakdown"
        >
          {predictionResultRows.map((row) => {
            const count = counts.get(row.result) ?? 0;
            const percentage = categorizedCount
              ? Math.round((count / categorizedCount) * 100)
              : 0;
            return (
              <div
                className="cp-expectancy-results__row"
                role="listitem"
                key={row.result}
              >
                <span className="cp-expectancy-results__label">
                  <span
                    className="cp-expectancy-results__dot"
                    data-tone={row.tone}
                    aria-hidden="true"
                  />
                  {row.result}
                </span>
                <span
                  className="cp-expectancy-results__track"
                  role="progressbar"
                  aria-label={`${row.result}: ${formatEntryCount(count)}, ${percentage} percent`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percentage}
                >
                  <span
                    className="cp-expectancy-results__fill"
                    data-tone={row.tone}
                    style={{ width: `${percentage}%` }}
                  />
                </span>
                <span className="cp-expectancy-results__count">{count}</span>
                <span className="cp-expectancy-results__percentage">
                  {percentage}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const columns: readonly PortalTableColumn<ExpectancyEntry>[] = [
  {
    key: "exposure",
    header: "Exposure",
    initialWidth: 220,
    minWidth: 150,
    cellClassName: "cp-table-cell--multiline",
    render: (entry) => textOrDash(entry.exposure),
  },
  {
    key: "obsession",
    header: "Obsession (Feared Prediction)",
    initialWidth: 270,
    minWidth: 190,
    cellClassName: "cp-table-cell--multiline",
    render: (entry) => textOrDash(entry.obsession),
  },
  {
    key: "timeSinceExposure",
    header: "Time Since Exposure",
    initialWidth: 175,
    minWidth: 135,
    cellClassName: "cp-table-cell--multiline",
    render: (entry) => textOrDash(entry.timeSinceExposure),
  },
  {
    key: "predictionResult",
    header: "Prediction Result",
    initialWidth: 180,
    minWidth: 145,
    render: (entry) => <ExpectancyResultPill result={entry.predictionResult} />,
  },
  {
    key: "whatILearned",
    header: "What I Learned",
    initialWidth: 280,
    minWidth: 190,
    cellClassName: "cp-table-cell--multiline",
    render: (entry) => textOrDash(entry.whatILearned),
  },
  {
    key: "createdAt",
    header: "Created",
    initialWidth: 190,
    minWidth: 165,
    render: (entry) => formatPortalDateTime(entry.createdAt),
  },
  {
    key: "createdByUser",
    header: "Created by User",
    initialWidth: 180,
    minWidth: 140,
    cellClassName: "cp-table-cell--multiline",
    render: (entry) => textOrDash(entry.createdByUser),
  },
];

function ExpectationsSkeleton() {
  return (
    <section
      className="cp-expectancies-skeleton"
      role="status"
      aria-label="Loading expectancy entries"
    >
      <span className="cp-visually-hidden">Loading expectancy entries</span>
      <div className="cp-expectancies-skeleton__heading" />
      <div className="cp-expectancies-skeleton__table">
        {Array.from({ length: 4 }, (_, index) => (
          <div className="cp-expectancies-skeleton__row" key={index} />
        ))}
      </div>
    </section>
  );
}

function ExpectationsError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="cp-inline-notice cp-inline-notice--error" role="alert">
      <span>Expectancy entries could not be loaded.</span>
      <button type="button" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function ClientExpectationsContent({
  clinicianCode,
  patientId,
  onContextLoaded,
  accessScope,
}: {
  clinicianCode: string;
  patientId: string;
  onContextLoaded?: (
    therapist: PortalTherapist,
    patient: PortalPatient,
  ) => void;
  accessScope: ClientAccessScope;
}) {
  const state = useClientExpectancies(clinicianCode, patientId, accessScope);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist &&
      data.patient?.id === patientId,
  );
  const entries = useMemo(
    () =>
      available
        ? (data?.expectancies ?? []).filter(
            (entry) => entry.patientId === patientId,
          )
        : [],
    [available, data?.expectancies, patientId],
  );

  useEffect(() => {
    if (available && data?.therapist && data.patient) {
      onContextLoaded?.(data.therapist, data.patient);
    }
  }, [available, data?.patient, data?.therapist, onContextLoaded]);

  if (state.status === "loading") return <ExpectationsSkeleton />;
  if (state.status === "error") {
    return <ExpectationsError onRetry={state.refresh} />;
  }
  if (!available) return <ExpectationsError onRetry={state.refresh} />;

  return (
    <ContentSection title="Expectancies" hideTitle>
      <p className="cp-content-section__description">
        Review the client’s Expectancy Tracker entries, newest first.
      </p>
      {entries.length ? (
        <>
          <ExpectancyResultsSummary entries={entries} />
          <PortalDataTable
            columns={columns}
            records={entries}
            getRecordKey={(entry) => entry.id}
            caption="Client expectancy entries"
          />
        </>
      ) : (
        <EmptyState
          title="No expectancy entries yet."
          description="Expectancy Tracker entries will appear here when they are available."
        />
      )}
    </ContentSection>
  );
}

export function ClientExpectationsSection({
  clinicianCode,
  patientId,
  onContextLoaded,
  readOnly: _readOnly = false,
  accessScope = "assigned",
}: {
  clinicianCode: string;
  patientId: string;
  onContextLoaded?: (
    therapist: PortalTherapist,
    patient: PortalPatient,
  ) => void;
  readOnly?: boolean;
  accessScope?: ClientAccessScope;
}) {
  const normalizedClinicianCode = clinicianCode.trim();
  const normalizedPatientId = patientId.trim();

  if (!normalizedClinicianCode || !normalizedPatientId) {
    return <ExpectationsSkeleton />;
  }

  return (
    <ClientExpectationsContent
      key={`${normalizedClinicianCode}:${normalizedPatientId}`}
      clinicianCode={normalizedClinicianCode}
      patientId={normalizedPatientId}
      onContextLoaded={onContextLoaded}
      accessScope={accessScope}
    />
  );
}
